import { useMemo, useState } from "react"
import { Button } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import { PettyCashBillListHeader } from "./PettyCashBillListHeader"
import PettyCashBillAddEdit from "./PettyCashBillAddEdit"
import PettyCashBillView from "./PettyCashBillView"
import { useListParams } from "hooks/useListParams"
import {
  usePettyCashBills,
  useDeletePettyCashBill,
} from "hooks/usePettyCashBills"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"

const PettyCashBillList = () => {
  const { params, setPage, resetParams, updateParams } = useListParams({
    page: 1,
    search: "",
  })

  const [modal, setModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [bill, setBill] = useState(null)
  const [isEdit, setIsEdit] = useState(false)

  // Hooks
  const { data: billsResponse, isLoading, refetch } = usePettyCashBills(params)
  const { mutateAsync: deleteBill } = useDeletePettyCashBill()

  const toggleModal = () => setModal(!modal)
  const toggleViewModal = () => setViewModal(!viewModal)

  const handleAddNewClick = () => {
    setBill(null)
    setIsEdit(false)
    toggleModal()
  }

  const handleEditClick = item => {
    setBill(item)
    setIsEdit(true)
    toggleModal()
  }

  const handleViewClick = item => {
    setBill(item)
    toggleViewModal()
  }

  const handleDeleteClick = item => {
    setBill(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!bill?.id) return
    await deleteBill(bill.id)
    setDeleteModal(false)
    refetch()
  }

  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()

  // Options
  const paymentModesData = useMemo(
    () => rawData?.petty_cash?.payment_modes ?? [],
    [rawData]
  )
  const paymentForsData = useMemo(
    () => rawData?.petty_cash?.payment_fors ?? [],
    [rawData]
  )
  const paymentFromsData = useMemo(
    () => rawData?.petty_cash?.payment_froms ?? [],
    [rawData]
  )
  const employeesData = useMemo(() => rawData?.employees ?? [], [rawData])

  const columns = useMemo(
    () => [
      { header: "Bill No", accessorKey: "bill_no" },
      { header: "Bill Date", accessorKey: "bill_date" },
      { header: "Total Amount", accessorKey: "total_amount" },
      { header: "Paid", accessorKey: "paid_amount" },
      { header: "Due", accessorKey: "due_amount" },
      { header: "Status", accessorKey: "status" },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="hstack gap-1">
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewClick(row.original)}
            >
              <i className="mdi mdi-eye" />
            </Button>
            <Button
              color="success"
              size="sm"
              onClick={() => handleEditClick(row.original)}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDeleteClick(row.original)}
            >
              <i className="mdi mdi-delete-outline" />
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <>
      <PettyCashBillListHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        total={billsResponse?.data?.total ?? 0}
        isRefetching={false}
      />

      <TableContainer
        columns={columns}
        data={billsResponse?.data?.data || []}
        isPagination={true}
        paginationInfo={billsResponse?.data}
        onPageChange={setPage}
        loading={isLoading}
      />

      <PettyCashBillAddEdit
        modal={modal}
        toggleModal={toggleModal}
        isEdit={isEdit}
        bill={bill}
        paymentModesData={paymentModesData}
        paymentForsData={paymentForsData}
        paymentFromsData={paymentFromsData}
        submittedData={employeesData}
      />

      <PettyCashBillView
        modal={viewModal}
        toggleModal={toggleViewModal}
        bill={bill}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
    </>
  )
}

export default PettyCashBillList
